import { getValueCookie } from '../services/cookieService'
import { getPurePriceMargin } from '../functions/generalFunctions'

/**
 * Builds one Invoice line from a /v3/salesorder/catalog row — the InvoiceV3
 * counterpart of SalesOrderV3/buildCartLine.js. Both share the same v3
 * catalogue endpoint and CatalogueV3.js component (2026-08-20, see
 * CatalogueV3.js's header comment), but InvoiceV3.js's Products array uses a
 * different shape (quantityShipped/totalWeight, not quantityOrdered), and
 * mirrors the legacy field set SearchUPC.js already builds for its own
 * 'Invoice' branch (ItemCode/LastTotalUnitCost/StandardUnitCost/etc.) so the
 * /invoice/set payload stays exactly what the backend already expects.
 *
 * Price/cost come ONLY from product_pricing (already joined into `row` by the
 * backend) — never calculated here. If standardPrice is null, the line still
 * gets added (so the user isn't blocked from building the rest of the
 * invoice) but flagged via `_noPricingConfigured`.
 */
export function buildInvoiceCartLine(row, quantity, customerName) {
    const unitPrice = (row.standardPrice !== null && row.standardPrice !== undefined) ? Number(row.standardPrice) : 0
    const cost = (row.cost !== null && row.cost !== undefined) ? Number(row.cost) : null
    const noPricingConfigured = row.standardPrice === null || row.standardPrice === undefined
    const shipWeight = Number(row.shipWeight) || 0

    // Long description (2026-08-20, client's ask) — sourced from CI_Item
    // (mas90_mirror__CI_Item / alt__mas90_mirror__CI_Item, joined into `row`
    // as `longDescription` by salesOrderCatalog.ts), NOT itemHeader.completeDesc
    // — the short `description` is the last-resort fallback for an item CI_Item
    // hasn't synced yet. Mirrors SalesOrderV3/buildCartLine.js (2026-08-20).
    const longDesc = row.longDescription || row.completeDesc || row.description

    const line = {
        itemCode: row.itemCode,
        abbreviatedDesc: longDesc,
        completeDesc: longDesc,
        itemURLImage: row.itemURLImage,
        upc: row.upc,
        upc2: row.upc2,
        caseupc: row.caseupc,
        quantity: row.quantity || 0, // on-hand, display only
        ShipWeight: shipWeight,
        quantityOrdered: 0,
        quantityShipped: quantity,
        quantityReceived: 0,
        UnitPrice: unitPrice,
        totalCost: unitPrice * quantity,
        totalWeight: shipWeight * quantity,
        totalFreight: 0,
        unitCost: cost,
        LastTotalUnitCost: cost,
        StandardUnitCost: cost,
        // Standardized pricing (2026-08-20) — itemCode -> {cost, minPrice,
        // maxPrice, level1Price} used by InvoiceV3.js's price-floor
        // enforcement and red/amber classification. Same fields
        // SalesOrderV3's buildCartLine.js attaches.
        minPrice: (row.minPrice !== null && row.minPrice !== undefined) ? Number(row.minPrice) : null,
        maxPrice: (row.maxPrice !== null && row.maxPrice !== undefined) ? Number(row.maxPrice) : null,
        level1Price: (row.level1Price !== null && row.level1Price !== undefined) ? Number(row.level1Price) : null,
        // Level 3 below-minimum exception (2026-08-20, migration 017) — when
        // set for this item+company, the price-floor enforcement uses cost
        // instead of minPrice — see InvoiceV3.js's priceFloorFor(). Mirrors
        // SalesOrderV3/buildCartLine.js.
        allowLevel3BelowCost: !!row.allowLevel3BelowCost,
        costAtSale: cost,
        standardPriceAtSale: unitPrice,
        finalPrice: unitPrice,
        Visible: 1,
        LineSequence: 0,
        Comment: '',
        PureMargin: undefined,
        RegisterType: 's',
        HaveUPC: !!(row.upc || row.upc2 || row.caseupc),
        // A freshly-added catalogue line is always priced at standardPrice —
        // starts 'ok' and counted toward the invoice total.
        PriceStatus: 'ok',
        AffectsInvoiceTotal: quantity > 0,
        _pricingApplied: true,
        _noPricingConfigured: noPricingConfigured,
    }

    // Altitude box-tracking — same fields SearchUPC.js/SalesOrderV3.js set.
    if (getValueCookie('CompanyId') === '6') {
        line.Comment = 'BOX:' + (customerName || '')
        line.PureMargin = getPurePriceMargin(unitPrice, cost)
    }

    return line
}
